import React, { useState, useEffect, useCallback } from 'react';
import {
  Database, Users, Tag, Settings, Upload, Plus, Trash2, Edit2, Search,
  RefreshCw, Save, Download, ChevronRight, Eye, Loader2, X, FileSpreadsheet,
  AlertTriangle, CheckCircle, Clock, ChevronDown, Layers, Info, Phone,
  Building2, User, Calendar, History, MoreHorizontal, Filter, FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const COLUMN_TYPES = [
  { value: 'string', label: 'Text', icon: '📝' },
  { value: 'integer', label: 'Number', icon: '🔢' },
  { value: 'decimal', label: 'Decimal', icon: '💯' },
  { value: 'email', label: 'Email', icon: '📧' },
  { value: 'date', label: 'Date', icon: '📅' },
  { value: 'phone', label: 'Phone', icon: '📱' },
  { value: 'url', label: 'URL', icon: '🔗' },
];

const TAG_COLORS = [
  '#6366f1', '#8b5cf6', '#d946ef', '#ec4899', '#f43f5e',
  '#ef4444', '#f97316', '#f59e0b', '#22c55e', '#10b981',
  '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6b7280'
];

// Format date to IST
const formatIST = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

const MasterLeadDataPage = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('data');
  const [stats, setStats] = useState({});

  // Data state
  const [data, setData] = useState([]);
  const [dataTotal, setDataTotal] = useState(0);
  const [dataPage, setDataPage] = useState(0);
  const [dataLimit, setDataLimit] = useState(100);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Grouped view state
  const [groupedData, setGroupedData] = useState([]);
  const [groupedTotal, setGroupedTotal] = useState(0);
  const [groupedPage, setGroupedPage] = useState(0);
  const [groupedSearch, setGroupedSearch] = useState('');

  // Columns state
  const [columns, setColumns] = useState([]);
  const [columnDialog, setColumnDialog] = useState(false);
  const [editingColumn, setEditingColumn] = useState(null);
  const [columnForm, setColumnForm] = useState({});

  // Tags state
  const [tags, setTags] = useState([]);
  const [tagDialog, setTagDialog] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [tagForm, setTagForm] = useState({ color: '#6366f1' });

  // Import state
  const [imports, setImports] = useState([]);
  const [importsTotal, setImportsTotal] = useState(0);

  // Upload state
  const [uploadDialog, setUploadDialog] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvColumns, setCsvColumns] = useState([]);
  const [csvPreview, setCsvPreview] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [uploadTags, setUploadTags] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Edit data state
  const [dataDialog, setDataDialog] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [dataForm, setDataForm] = useState({});

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [confirmCount, setConfirmCount] = useState(0);

  // Fetch functions
  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/api/master-lead-data/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  const fetchData = useCallback(async (page = 0, limit = 100, search = '') => {
    try {
      let url = `/api/master-lead-data/data?skip=${page * limit}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await api.get(url);
      setData(res.data.data || []);
      setDataTotal(res.data.total || 0);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to fetch data', description: error.response?.data?.detail });
    }
  }, []);

  const fetchColumns = useCallback(async () => {
    try {
      const res = await api.get('/api/master-lead-data/columns');
      setColumns(res.data.columns || []);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to fetch columns' });
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const res = await api.get('/api/master-lead-data/tags');
      setTags(res.data.tags || []);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to fetch tags' });
    }
  }, []);

  const fetchImports = useCallback(async () => {
    try {
      const res = await api.get('/api/master-lead-data/imports?limit=50');
      setImports(res.data.imports || []);
      setImportsTotal(res.data.total || 0);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to fetch imports' });
    }
  }, []);

  const fetchGroupedData = useCallback(async (page = 0, search = '') => {
    try {
      let url = `/api/master-lead-data/data/grouped-by-mobile?skip=${page * 50}&limit=50`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await api.get(url);
      setGroupedData(res.data.rows || []);
      setGroupedTotal(res.data.total || 0);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to fetch grouped data', description: error.response?.data?.detail });
    }
  }, []);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchColumns(), fetchTags()]);
      setLoading(false);
    };
    init();
  }, [fetchStats, fetchColumns, fetchTags]);

  // Tab change handler
  useEffect(() => {
    if (activeTab === 'data') fetchData(dataPage, dataLimit, searchQuery);
    if (activeTab === 'grouped') fetchGroupedData(groupedPage, groupedSearch);
    if (activeTab === 'imports') fetchImports();
  }, [activeTab, dataPage, dataLimit, searchQuery, groupedPage, groupedSearch, fetchData, fetchGroupedData, fetchImports]);

  // Column CRUD
  const saveColumn = async () => {
    try {
      if (editingColumn) {
        await api.patch(`/api/master-lead-data/columns/${editingColumn.id}`, columnForm);
        toast({ title: 'Column updated' });
      } else {
        await api.post('/api/master-lead-data/columns', columnForm);
        toast({ title: 'Column created' });
      }
      setColumnDialog(false);
      setEditingColumn(null);
      setColumnForm({});
      fetchColumns();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed', description: error.response?.data?.detail });
    }
  };

  const deleteColumn = async (id) => {
    try {
      await api.delete(`/api/master-lead-data/columns/${id}`);
      toast({ title: 'Column deleted' });
      fetchColumns();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed', description: error.response?.data?.detail });
    }
  };

  // Tag CRUD
  const saveTag = async () => {
    try {
      if (editingTag) {
        await api.patch(`/api/master-lead-data/tags/${editingTag.id}`, tagForm);
        toast({ title: 'Tag updated' });
      } else {
        const res = await api.post('/api/master-lead-data/tags', tagForm);
        toast({ title: 'Tag created' });
        // Return new tag for inline creation
        return res.data.tag;
      }
      setTagDialog(false);
      setEditingTag(null);
      setTagForm({ color: '#6366f1' });
      fetchTags();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed', description: error.response?.data?.detail });
    }
    return null;
  };

  const deleteTag = async (id) => {
    try {
      await api.delete(`/api/master-lead-data/tags/${id}`);
      toast({ title: 'Tag deleted' });
      fetchTags();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed', description: error.response?.data?.detail });
    }
  };

  // Data CRUD
  const saveData = async () => {
    try {
      if (editingData) {
        await api.patch(`/api/master-lead-data/data/${editingData.id}`, dataForm);
        toast({ title: 'Data updated' });
      } else {
        await api.post('/api/master-lead-data/data', dataForm);
        toast({ title: 'Data created' });
      }
      setDataDialog(false);
      setEditingData(null);
      setDataForm({});
      fetchData(dataPage, dataLimit, searchQuery);
      fetchStats();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed', description: error.response?.data?.detail });
    }
  };

  const handleDelete = async () => {
    if (confirmCount < 3) {
      setConfirmCount(confirmCount + 1);
      return;
    }

    try {
      const { type, item, items } = deleteConfirm;

      if (type === 'single') {
        await api.delete(`/api/master-lead-data/data/${item.id}?confirmation_count=3`);
        toast({ title: 'Data deleted' });
      } else if (type === 'bulk') {
        await api.post('/api/master-lead-data/data/bulk-delete', {
          ids: items.map(i => i.id),
          confirmation_count: 3
        });
        toast({ title: `Deleted ${items.length} rows` });
      } else if (type === 'import') {
        await api.delete(`/api/master-lead-data/imports/${item.id}?delete_data=true&confirmation_count=3`);
        toast({ title: 'Import and data deleted' });
        fetchImports();
      } else if (type === 'all') {
        await api.post('/api/master-lead-data/data/delete-all', { confirmation_count: 3 });
        toast({ title: 'All data deleted' });
      }

      setDeleteConfirm(null);
      setConfirmCount(0);
      setSelectedRows([]);
      setSelectAll(false);
      fetchData(dataPage, dataLimit, searchQuery);
      fetchStats();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed', description: error.response?.data?.detail });
    }
  };

  // CSV Upload
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      const lines = text.split('\n');
      const cols = lines[0].split(',').map(col => col.trim().replace(/"/g, ''));
      setCsvColumns(cols);
      const preview = [];
      for (let i = 1; i < Math.min(6, lines.length); i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
          const row = {};
          cols.forEach((col, idx) => { row[col] = values[idx] || ''; });
          preview.push(row);
        }
      }
      setCsvPreview(preview);
      // Auto-map columns
      const autoMapping = {};
      cols.forEach(col => {
        const lower = col.toLowerCase();
        if (lower.includes('mobile') || lower.includes('phone') || lower === 'mob') {
          autoMapping[col] = 'mobile_no';
        } else if (lower.includes('company') || lower.includes('org')) {
          autoMapping[col] = 'company_name';
        } else if (lower === 'name' || lower.includes('contact')) {
          autoMapping[col] = 'name';
        }
      });
      setColumnMapping(autoMapping);
    };
    reader.readAsText(file);
  };

  const uploadCSV = async () => {
    if (!csvFile) return;

    // Validate mandatory mapping
    const mappedValues = Object.values(columnMapping);
    if (!mappedValues.includes('mobile_no')) {
      toast({ variant: 'destructive', title: 'Mobile Number column must be mapped' });
      return;
    }
    if (!mappedValues.includes('company_name')) {
      toast({ variant: 'destructive', title: 'Company Name column must be mapped' });
      return;
    }

    setUploading(true);
    setUploadProgress(10);

    const formData = new FormData();
    formData.append('file', csvFile);
    formData.append('column_mapping', JSON.stringify(columnMapping));
    if (uploadTags.length) formData.append('tags', uploadTags.join(','));

    try {
      setUploadProgress(30);
      const res = await api.post('/api/master-lead-data/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadProgress(Math.min(Math.round((e.loaded * 60) / e.total) + 30, 90))
      });
      setUploadProgress(100);
      toast({
        title: 'Upload completed',
        description: `Success: ${res.data.success}, Failed: ${res.data.failed}`
      });
      setUploadDialog(false);
      resetUploadState();
      fetchData(0, dataLimit, '');
      fetchStats();
      fetchImports();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Upload failed', description: error.response?.data?.detail });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const resetUploadState = () => {
    setCsvFile(null);
    setCsvColumns([]);
    setCsvPreview([]);
    setColumnMapping({});
    setUploadTags([]);
  };

  const downloadImportFile = async (importId, filename) => {
    try {
      const res = await api.get(`/api/master-lead-data/imports/${importId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Download failed' });
    }
  };

  // Row selection
  const toggleRowSelection = (row) => {
    setSelectedRows(prev => {
      const exists = prev.find(r => r.id === row.id);
      if (exists) return prev.filter(r => r.id !== row.id);
      return [...prev, row];
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      setSelectedRows([...data]);
    }
    setSelectAll(!selectAll);
  };

  // Custom columns (non-system)
  const customColumns = columns.filter(c => !c.is_system);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="w-7 h-7 text-emerald-600" />
            Master Lead Data
          </h1>
          <p className="text-slate-500">Manage lead master data for WhatsApp AI enrichment</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setUploadDialog(true)}>
            <Upload className="w-4 h-4 mr-2" />Upload CSV
          </Button>
          <Button onClick={() => { setEditingData(null); setDataForm({}); setDataDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />Add Data
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stats.total_data?.toLocaleString() || 0}</p>
                <p className="text-xs text-slate-500">Total Records</p>
              </div>
              <Database className="w-8 h-8 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stats.unique_mobiles?.toLocaleString() || 0}</p>
                <p className="text-xs text-slate-500">Unique Mobiles</p>
              </div>
              <Phone className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stats.total_imports || 0}</p>
                <p className="text-xs text-slate-500">Imports</p>
              </div>
              <History className="w-8 h-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stats.custom_columns || 0}</p>
                <p className="text-xs text-slate-500">Custom Columns</p>
              </div>
              <Layers className="w-8 h-8 text-orange-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stats.total_tags || 0}</p>
                <p className="text-xs text-slate-500">Tags</p>
              </div>
              <Tag className="w-8 h-8 text-pink-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="data"><Database className="w-4 h-4 mr-1" />Data</TabsTrigger>
          <TabsTrigger value="grouped"><Users className="w-4 h-4 mr-1" />Grouped View</TabsTrigger>
          <TabsTrigger value="columns"><Layers className="w-4 h-4 mr-1" />Columns</TabsTrigger>
          <TabsTrigger value="tags"><Tag className="w-4 h-4 mr-1" />Tags</TabsTrigger>
          <TabsTrigger value="imports"><History className="w-4 h-4 mr-1" />Import History</TabsTrigger>
        </TabsList>

        {/* Data Tab */}
        <TabsContent value="data" className="space-y-4">
          {/* Search & Actions */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by mobile, name, company..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchData(0, dataLimit, searchQuery)}
                  className="pl-10"
                />
              </div>
              <Button variant="outline" onClick={() => fetchData(0, dataLimit, searchQuery)}>
                <Search className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {selectedRows.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeleteConfirm({ type: 'bulk', items: selectedRows })}
                >
                  <Trash2 className="w-4 h-4 mr-1" />Delete {selectedRows.length}
                </Button>
              )}
              <Select value={String(dataLimit)} onValueChange={(v) => { setDataLimit(Number(v)); setDataPage(0); }}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="100">100 rows</SelectItem>
                  <SelectItem value="250">250 rows</SelectItem>
                  <SelectItem value="500">500 rows</SelectItem>
                </SelectContent>
              </Select>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    className="text-red-600"
                    onClick={() => setDeleteConfirm({ type: 'all' })}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />Delete All Data
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Data Table */}
          <Card>
            <CardContent className="p-0">
              <div className="border rounded-lg overflow-auto max-h-[600px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10">
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox checked={selectAll} onCheckedChange={toggleSelectAll} />
                      </TableHead>
                      <TableHead>Mobile No</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Company Name</TableHead>
                      <TableHead>Tags</TableHead>
                      <TableHead>Created At (IST)</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                          No data found. Upload a CSV to get started.
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.map((row) => (
                        <TableRow key={row.id} className={selectedRows.find(r => r.id === row.id) ? 'bg-emerald-50' : ''}>
                          <TableCell>
                            <Checkbox
                              checked={!!selectedRows.find(r => r.id === row.id)}
                              onCheckedChange={() => toggleRowSelection(row)}
                            />
                          </TableCell>
                          <TableCell className="font-mono">{row.mobile_no}</TableCell>
                          <TableCell>{row.name || '-'}</TableCell>
                          <TableCell className="max-w-[200px] truncate" title={row.company_name}>{row.company_name}</TableCell>
                          <TableCell>
                            <div className="flex gap-1 flex-wrap">
                              {(row.tags || []).slice(0, 2).map(tagId => {
                                const tag = tags.find(t => t.id === tagId);
                                return tag ? (
                                  <Badge key={tagId} variant="outline" style={{ borderColor: tag.color, color: tag.color }} className="text-[10px]">
                                    {tag.name}
                                  </Badge>
                                ) : null;
                              })}
                              {(row.tags || []).length > 2 && (
                                <Badge variant="outline" className="text-[10px]">+{row.tags.length - 2}</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">{formatIST(row.created_at)}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingData(row);
                                  setDataForm({
                                    name: row.name,
                                    company_name: row.company_name,
                                    custom_data: row.custom_data,
                                    tags: row.tags
                                  });
                                  setDataDialog(true);
                                }}
                              >
                                <Edit2 className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600"
                                onClick={() => setDeleteConfirm({ type: 'single', item: row })}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Showing {dataPage * dataLimit + 1} - {Math.min((dataPage + 1) * dataLimit, dataTotal)} of {dataTotal.toLocaleString()}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={dataPage === 0}
                onClick={() => setDataPage(dataPage - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={(dataPage + 1) * dataLimit >= dataTotal}
                onClick={() => setDataPage(dataPage + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Grouped View Tab */}
        <TabsContent value="grouped" className="space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                Contacts Grouped by Mobile Number
              </CardTitle>
              <CardDescription>
                View all contacts grouped by their mobile number, showing all associated companies for each contact.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search */}
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by mobile, name, or company..."
                    value={groupedSearch}
                    onChange={(e) => setGroupedSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchGroupedData(0, groupedSearch)}
                    className="pl-10"
                  />
                </div>
                <Button variant="outline" onClick={() => fetchGroupedData(groupedPage, groupedSearch)}>
                  <RefreshCw className="w-4 h-4 mr-2" />Refresh
                </Button>
              </div>

              {/* Total count */}
              <div className="text-sm text-slate-500">
                {groupedTotal.toLocaleString()} unique mobile numbers
              </div>

              {/* Grouped Data Table */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-[140px]">
                        <div className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />Mobile
                        </div>
                      </TableHead>
                      <TableHead className="w-[180px]">
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />Name
                        </div>
                      </TableHead>
                      <TableHead>
                        <div className="flex items-center gap-1">
                          <Building2 className="w-4 h-4" />Companies
                        </div>
                      </TableHead>
                      <TableHead className="w-[100px] text-center">Count</TableHead>
                      <TableHead className="w-[140px]">First Added</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupedData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                          No data found. Upload CSV to add contacts.
                        </TableCell>
                      </TableRow>
                    ) : (
                      groupedData.map((row, idx) => (
                        <TableRow key={row.mobile_no || idx} className="hover:bg-slate-50">
                          <TableCell className="font-mono text-sm">{row.mobile_no}</TableCell>
                          <TableCell className="font-medium">{row.name || '-'}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {(row.companies || []).map((company, i) => (
                                <Badge key={i} variant="outline" className="text-xs bg-blue-50 border-blue-200 text-blue-700">
                                  <Building2 className="w-3 h-3 mr-1" />
                                  {company}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant={row.company_count > 1 ? 'default' : 'secondary'} className={row.company_count > 1 ? 'bg-purple-600' : ''}>
                              {row.company_count}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">{formatIST(row.first_added)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">
                  Showing {groupedPage * 50 + 1} - {Math.min((groupedPage + 1) * 50, groupedTotal)} of {groupedTotal.toLocaleString()}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setGroupedPage(Math.max(0, groupedPage - 1))}
                    disabled={groupedPage === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setGroupedPage(groupedPage + 1)}
                    disabled={(groupedPage + 1) * 50 >= groupedTotal}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Columns Tab */}
        <TabsContent value="columns" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">Define columns for your lead data. System columns cannot be modified.</p>
            <Button onClick={() => { setEditingColumn(null); setColumnForm({ column_type: 'string' }); setColumnDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" />Add Column
            </Button>
          </div>

          <div className="grid gap-3">
            {columns.map((col) => (
              <Card key={col.id} className={col.is_system ? 'border-emerald-200 bg-emerald-50/50' : ''}>
                <CardContent className="py-3 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-lg">
                      {COLUMN_TYPES.find(t => t.value === col.column_type)?.icon || '📝'}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{col.display_name}</span>
                        {col.is_system && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">System</Badge>}
                        {col.is_mandatory && <Badge variant="outline" className="text-[10px]">Required</Badge>}
                      </div>
                      <p className="text-xs text-slate-500">{col.name} • {col.column_type}</p>
                    </div>
                  </div>
                  {!col.is_system && (
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => {
                        setEditingColumn(col);
                        setColumnForm({ display_name: col.display_name, description: col.description, is_mandatory: col.is_mandatory });
                        setColumnDialog(true);
                      }}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteColumn(col.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Tags Tab */}
        <TabsContent value="tags" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">Create tags to organize your lead data.</p>
            <Button onClick={() => { setEditingTag(null); setTagForm({ color: '#6366f1' }); setTagDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" />Add Tag
            </Button>
          </div>

          <div className="grid grid-cols-4 gap-3">
            {tags.map((tag) => (
              <Card key={tag.id}>
                <CardContent className="py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: tag.color }} />
                    <span className="font-medium">{tag.name}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => {
                      setEditingTag(tag);
                      setTagForm({ name: tag.name, color: tag.color, description: tag.description });
                      setTagDialog(true);
                    }}>
                      <Edit2 className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteTag(tag.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {tags.length === 0 && (
              <p className="col-span-4 text-center py-8 text-slate-500">No tags created yet.</p>
            )}
          </div>
        </TabsContent>

        {/* Import History Tab */}
        <TabsContent value="imports" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">View all CSV imports and download original files.</p>
            <Button variant="outline" onClick={fetchImports}>
              <RefreshCw className="w-4 h-4 mr-2" />Refresh
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Filename</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Rows</TableHead>
                    <TableHead>Success</TableHead>
                    <TableHead>Failed</TableHead>
                    <TableHead>Uploaded By</TableHead>
                    <TableHead>Date (IST)</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {imports.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                        No imports yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    imports.map((imp) => (
                      <TableRow key={imp.id}>
                        <TableCell className="font-medium">{imp.filename}</TableCell>
                        <TableCell>{formatFileSize(imp.file_size_bytes)}</TableCell>
                        <TableCell>{imp.total_rows?.toLocaleString()}</TableCell>
                        <TableCell className="text-emerald-600">{imp.success_count?.toLocaleString()}</TableCell>
                        <TableCell className="text-red-600">{imp.failed_count || 0}</TableCell>
                        <TableCell>{imp.uploaded_by_name || '-'}</TableCell>
                        <TableCell className="text-xs">{formatIST(imp.created_at)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => downloadImportFile(imp.id, imp.filename)}>
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600"
                              onClick={() => setDeleteConfirm({ type: 'import', item: imp })}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Column Dialog */}
      <Dialog open={columnDialog} onOpenChange={setColumnDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingColumn ? 'Edit Column' : 'Add Column'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editingColumn && (
              <>
                <div className="space-y-2">
                  <Label>Column Name (internal)</Label>
                  <Input
                    value={columnForm.name || ''}
                    onChange={(e) => setColumnForm(f => ({ ...f, name: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                    placeholder="e.g., industry_type"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Column Type</Label>
                  <Select value={columnForm.column_type || 'string'} onValueChange={(v) => setColumnForm(f => ({ ...f, column_type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COLUMN_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.icon} {t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>Display Name</Label>
              <Input
                value={columnForm.display_name || ''}
                onChange={(e) => setColumnForm(f => ({ ...f, display_name: e.target.value }))}
                placeholder="e.g., Industry Type"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                value={columnForm.description || ''}
                onChange={(e) => setColumnForm(f => ({ ...f, description: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setColumnDialog(false)}>Cancel</Button>
            <Button onClick={saveColumn}>{editingColumn ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag Dialog */}
      <Dialog open={tagDialog} onOpenChange={setTagDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTag ? 'Edit Tag' : 'Add Tag'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tag Name</Label>
              <Input
                value={tagForm.name || ''}
                onChange={(e) => setTagForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g., VIP Customer"
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex gap-2 flex-wrap">
                {TAG_COLORS.map(color => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${tagForm.color === color ? 'border-slate-900 scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setTagForm(f => ({ ...f, color }))}
                  />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                value={tagForm.description || ''}
                onChange={(e) => setTagForm(f => ({ ...f, description: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTagDialog(false)}>Cancel</Button>
            <Button onClick={saveTag}>{editingTag ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Data Edit Dialog */}
      <Dialog open={dataDialog} onOpenChange={setDataDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingData ? 'Edit Record' : 'Add Record'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Mobile Number *</Label>
              <Input
                value={dataForm.mobile_no || ''}
                onChange={(e) => setDataForm(f => ({ ...f, mobile_no: e.target.value }))}
                placeholder="10-digit mobile number"
                disabled={!!editingData}
              />
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={dataForm.name || ''}
                onChange={(e) => setDataForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input
                value={dataForm.company_name || ''}
                onChange={(e) => setDataForm(f => ({ ...f, company_name: e.target.value }))}
              />
            </div>
            {tags.length > 0 && (
              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex gap-2 flex-wrap">
                  {tags.map(tag => (
                    <Badge
                      key={tag.id}
                      variant="outline"
                      className="cursor-pointer"
                      style={{
                        borderColor: tag.color,
                        backgroundColor: (dataForm.tags || []).includes(tag.id) ? `${tag.color}20` : 'transparent'
                      }}
                      onClick={() => {
                        const currentTags = dataForm.tags || [];
                        if (currentTags.includes(tag.id)) {
                          setDataForm(f => ({ ...f, tags: currentTags.filter(t => t !== tag.id) }));
                        } else {
                          setDataForm(f => ({ ...f, tags: [...currentTags, tag.id] }));
                        }
                      }}
                    >
                      <span style={{ color: tag.color }}>{tag.name}</span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDataDialog(false)}>Cancel</Button>
            <Button onClick={saveData} disabled={!dataForm.mobile_no || !dataForm.company_name}>
              {editingData ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={(o) => { if (!o) resetUploadState(); setUploadDialog(o); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle><FileSpreadsheet className="w-5 h-5 inline mr-2" />Upload CSV</DialogTitle>
            <DialogDescription>Import up to 1 million records with column mapping</DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* File Upload */}
            <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-emerald-400 transition-colors">
              <Input type="file" accept=".csv" onChange={handleFileChange} className="hidden" id="csv-upload" />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                <p className="font-medium">{csvFile ? csvFile.name : 'Click to upload CSV'}</p>
                <p className="text-xs text-slate-500 mt-1">Maximum 1 million rows • 100MB limit</p>
              </label>
            </div>

            {csvColumns.length > 0 && (
              <>
                {/* Column Mapping */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <Layers className="w-4 h-4 text-purple-500" />
                    Column Mapping
                    <span className="text-xs text-slate-500 font-normal">(Map CSV columns to database fields)</span>
                  </h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs w-1/2">CSV Column</TableHead>
                          <TableHead className="text-xs w-1/2">Map to Field</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {csvColumns.map(col => (
                          <TableRow key={col}>
                            <TableCell className="py-2">
                              <span className="text-sm font-medium">{col}</span>
                            </TableCell>
                            <TableCell className="py-2">
                              <Select
                                value={columnMapping[col] || 'skip'}
                                onValueChange={(v) => setColumnMapping(m => ({ ...m, [col]: v === 'skip' ? undefined : v }))}
                              >
                                <SelectTrigger className="h-8 text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="skip"><span className="text-slate-500">Skip</span></SelectItem>
                                  <SelectItem value="mobile_no">📱 Mobile Number *</SelectItem>
                                  <SelectItem value="company_name">🏢 Company Name *</SelectItem>
                                  <SelectItem value="name">👤 Name</SelectItem>
                                  {customColumns.map(cc => (
                                    <SelectItem key={cc.id} value={cc.name}>
                                      {COLUMN_TYPES.find(t => t.value === cc.column_type)?.icon || '📝'} {cc.display_name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {(!Object.values(columnMapping).includes('mobile_no') || !Object.values(columnMapping).includes('company_name')) && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Mobile Number and Company Name are required
                    </p>
                  )}
                </div>

                {/* Tags Selection */}
                {tags.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <Tag className="w-4 h-4 text-orange-500" />
                      Apply Tags to All Imported Records
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {tags.map(tag => (
                        <Badge
                          key={tag.id}
                          variant="outline"
                          className={`cursor-pointer transition-colors ${uploadTags.includes(tag.id) ? 'border-2' : 'opacity-60 hover:opacity-100'}`}
                          style={{
                            borderColor: tag.color,
                            backgroundColor: uploadTags.includes(tag.id) ? `${tag.color}20` : 'transparent'
                          }}
                          onClick={() => {
                            if (uploadTags.includes(tag.id)) {
                              setUploadTags(uploadTags.filter(t => t !== tag.id));
                            } else {
                              setUploadTags([...uploadTags, tag.id]);
                            }
                          }}
                        >
                          <span style={{ color: tag.color }}>{tag.name}</span>
                          {uploadTags.includes(tag.id) && <CheckCircle className="w-3 h-3 ml-1" style={{ color: tag.color }} />}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preview */}
                {csvPreview.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <Eye className="w-4 h-4 text-slate-500" />
                      Data Preview (First 5 rows)
                    </h4>
                    <div className="border rounded-lg overflow-auto max-h-40">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            {csvColumns.slice(0, 6).map(c => (
                              <TableHead key={c} className="text-xs whitespace-nowrap">{c}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {csvPreview.map((r, i) => (
                            <TableRow key={i}>
                              {csvColumns.slice(0, 6).map(c => (
                                <TableCell key={c} className="text-xs py-1.5">{r[c] || '-'}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Progress */}
            {uploading && (
              <div className="space-y-2 bg-blue-50 p-4 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Uploading...</span>
                  <span className="text-blue-600">{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="h-2" />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { resetUploadState(); setUploadDialog(false); }}>Cancel</Button>
            <Button
              onClick={uploadCSV}
              disabled={!csvFile || uploading || !Object.values(columnMapping).includes('mobile_no') || !Object.values(columnMapping).includes('company_name')}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</> : <><Upload className="w-4 h-4 mr-2" />Upload</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation with 3x Warning */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(o) => { if (!o) { setDeleteConfirm(null); setConfirmCount(0); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {confirmCount < 3 ? `Warning ${confirmCount + 1}/3` : 'Final Confirmation'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              {deleteConfirm?.type === 'single' && (
                <p>You are about to delete the record for <strong>{deleteConfirm.item?.company_name}</strong> ({deleteConfirm.item?.mobile_no}).</p>
              )}
              {deleteConfirm?.type === 'bulk' && (
                <p>You are about to delete <strong>{deleteConfirm.items?.length}</strong> selected records.</p>
              )}
              {deleteConfirm?.type === 'import' && (
                <p>You are about to delete import <strong>{deleteConfirm.item?.filename}</strong> and all {deleteConfirm.item?.success_count?.toLocaleString()} associated records.</p>
              )}
              {deleteConfirm?.type === 'all' && (
                <p className="text-red-600 font-medium">⚠️ You are about to delete ALL {stats.total_data?.toLocaleString()} records. This cannot be undone!</p>
              )}
              {confirmCount < 3 ? (
                <p className="text-amber-600 font-medium">Click &quot;{confirmCount === 2 ? 'Delete' : 'Continue'}&quot; {3 - confirmCount} more time(s) to confirm.</p>
              ) : (
                <p className="text-red-600 font-medium">This action is irreversible. Click &quot;Delete&quot; to proceed.</p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDeleteConfirm(null); setConfirmCount(0); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={confirmCount >= 2 ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'}
              onClick={handleDelete}
            >
              {confirmCount < 2 ? 'Continue' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MasterLeadDataPage;
